import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  TextField,
  Checkbox
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { Formik, Form, Field } from 'formik';

interface RadioComponentProps {
  values: any;
  index: number;
  childIndex: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
}

export default function RadioComponent({ values, index, childIndex, setFieldValue }: RadioComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [newOption, setNewOption] = useState('');

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-radio-popover' : undefined;

  const child = values.fields[index].children[childIndex];
  const options = child.options || ['Option 1', 'Option 2'];
  const settings = child.settings || {};
  const orientation = settings.orientation || 'vertical';

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <InputLabel sx={{ color: 'black' }}>{child.question}</InputLabel>
        </Grid>

        <Grid item xs={11}>
          <RadioGroup
            name={`fields.${index}.children.${childIndex}.answer`}
            value={child.answer || ''}
            onChange={(e) => setFieldValue(`fields.${index}.children.${childIndex}.answer`, e.target.value)}
            row={orientation === 'horizontal'}
          >
            {options.map((option: string, i: number) => (
              <FormControlLabel key={i} value={option} control={<Radio size="small" />} label={option} />
            ))}
          </RadioGroup>
        </Grid>

        <Grid item xs={1}>
          <Box sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
            <CreateIcon fontSize="medium" />
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Formik
          initialValues={{
            question: child.question || '',
            description: child.description || '',
            options: [...options],
            orientation: settings.orientation || 'vertical',
            required: settings.required || false,
            isSupportFileRequired: settings.isSupportFileRequired || false,
            noOfSupportImages: settings.noOfSupportImages || ''
          }}
          onSubmit={(formValues) => {
            const updatedChild = {
              ...child,
              question: formValues.question,
              description: formValues.description,
              options: formValues.options,
              settings: {
                ...settings,
                orientation: formValues.orientation,
                required: formValues.required,
                isSupportFileRequired: formValues.isSupportFileRequired,
                noOfSupportImages: formValues.isSupportFileRequired ? formValues.noOfSupportImages : ''
              }
            };
            setFieldValue(`fields.${index}.children.${childIndex}`, updatedChild);
            handleClosePopover();
          }}
        >
          {({ values: formValues, handleChange, handleBlur, setFieldValue: setFormikField }) => (
            <Form>
              <Typography variant="subtitle1" mb={2}>
                Edit Radio Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <InputLabel>Label</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="question" fullWidth size="small" onChange={handleChange} onBlur={handleBlur} />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Description</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="description" fullWidth size="small" onChange={handleChange} onBlur={handleBlur} />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel>Options</InputLabel>
                  {formValues.options.map((opt: string, i: number) => (
                    <Box key={i} display="flex" alignItems="center" mt={1}>
                      <TextField
                        fullWidth
                        size="small"
                        value={opt}
                        onChange={(e) => {
                          const updated = [...formValues.options];
                          updated[i] = e.target.value;
                          setFormikField('options', updated);
                        }}
                      />
                      <Button
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          const filtered = formValues.options.filter((_, idx) => idx !== i);
                          setFormikField('options', filtered);
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  ))}

                  <Box mt={2} display="flex">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="New Option"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={() => {
                        if (newOption.trim() !== '') {
                          setFormikField('options', [...formValues.options, newOption.trim()]);
                          setNewOption('');
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formValues.orientation === 'horizontal'}
                        onChange={(e) => setFormikField('orientation', e.target.checked ? 'horizontal' : 'vertical')}
                      />
                    }
                    label="Horizontal Orientation"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={formValues.required} onChange={(e) => setFormikField('required', e.target.checked)} />}
                    label="Required"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox name="isSupportFileRequired" checked={formValues.isSupportFileRequired} onChange={handleChange} />}
                    label="Support File Required"
                  />
                </Grid>

                {formValues.isSupportFileRequired && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="noOfSupportImages"
                      type="number"
                      label="Number of Images"
                      value={formValues.noOfSupportImages}
                      onChange={handleChange}
                      size="small"
                      inputProps={{ min: 1 }}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button type="submit" size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}
